import { useContext, useState } from "react";
import {
  getOrchestratorErrorsData,
  TransformedDataType as getOrchestratorErrorsData_TransformedDataType,
  ResponseGuiType,
} from "../../api/errors/getOrchestratorErrorsData";
import { exportToExcel } from "../../utils/exportToExcel";
import { Badge, Button, Tooltip } from "antd";
import TablePage from "../../components/table-page/TablePage";
import TextHighlighter from "../../utils/text-highlighter/TextHighlighter";
import {
  getSipNumber,
  TransformedDataType as getSipNumber_TransformedDataType,
} from "../../api/errors/getSipNumber";
import EditNumberStatusFormModal from "../numbers/edit-number-status-form-modal/EditNumberStatusFormModal";
import { GlobalContext } from "../../components/layout/global-context/globalContext";
import InfoCircleFilled from "@ant-design/icons/InfoCircleFilled";
import { AiOutlineFileExcel } from "react-icons/ai";

interface State {
  isEditNumberStatusModalVisible: boolean;
  editNumberRecord?: getSipNumber_TransformedDataType;
  refreshDataToggle: boolean;
  dataView: getOrchestratorErrorsData_TransformedDataType[];
}

function ErrorsPage() {
  const { user, setErrorsQtty } = useContext(GlobalContext);
  const [state, setState] = useState<State>({
    isEditNumberStatusModalVisible: false,
    editNumberRecord: undefined as getSipNumber_TransformedDataType | undefined,
    refreshDataToggle: true,
    dataView: [] as getOrchestratorErrorsData_TransformedDataType[],
  });

  const {
    editNumberRecord,
    isEditNumberStatusModalVisible,
    refreshDataToggle,
    dataView,
  } = state;

  const buttons = [
    {
      title: (
        <span className="flex">
          <AiOutlineFileExcel className="text-lg mr-1" />
          Export
        </span>
      ),
      onClick: () => {
        exportToExcel(dataView, "Export-Errors", {
          phoneNumber: { displayAs: "Phone Number", colWidth: 14 },
          usernameOrMeetingRoomNameX: {
            displayAs: "Username/Meeting Room/DL",
            colWidth: 28,
          },
          usernameOrMeetingRoomNameXType: {
            displayAs: "Username/Meeting Room/DL Type",
            colWidth: 30,
          },
          dateTimeAddedX: { displayAs: "Assigned Date", colWidth: 14 },
          createdBy: { displayAs: "Created By", colWidth: 20 },
          lastError: { displayAs: "Last Error", colWidth: 50 },
        });
      },
    },
  ];

  const getOrchestratorErrorsDataPromise =
    async (): Promise<ResponseGuiType> => {
      //LFC: await getOrchestratorErrorsData to be resolved before setting Errors badge
      const response = await getOrchestratorErrorsData();
      if (response.data) {
        setErrorsQtty(response.data.length);
      }
      return response;
    };

  return (
    <div className="flex flex-col w-full errors-page">
      <div className="flex justify-between items-center mx-8 my-6">
        <h1 className="text-lg font-semibold text-bdo-text-01-charcoal">
          Errors
        </h1>
        <aside className="flex gap-3">
          {buttons.map((button, index) => {
            return (
              <Button
                key={index}
                onClick={button.onClick}
                className="bdo-red-button"
              >
                {button.title}
              </Button>
            );
          })}
        </aside>
      </div>

      <div className="flex-row w-full">
        <TablePage
          refreshDataToggle={refreshDataToggle}
          setParentDataView={(data) => {
            setState((prevState) => ({ ...prevState, dataView: data }));
          }}
          pageConfiguration={{
            pageName: "ErrorsPage",
            dataApi: getOrchestratorErrorsDataPromise,
            columnDefinitions: [
              {
                titleText: "Number",
                dataIndex: "phoneNumberX",
                ignoreChars: " -()",
                render: ({
                  dataRow,
                  cellText = "",
                  searchText = "",
                  ignoreChars = "",
                }) => {
                  return user.isAdmin ? (
                    <div className="relative">
                      <Button
                        type="link"
                        onClick={() => {
                          const orchestratorErrorRecord =
                            dataRow as getOrchestratorErrorsData_TransformedDataType;
                          if (orchestratorErrorRecord.sipNumberId) {
                            getSipNumber(
                              orchestratorErrorRecord?.sipNumberId.toString()
                            ).then((sipNumberRecord) => {
                              console.log(
                                "sipNumberRecord: %o",
                                sipNumberRecord
                              );
                              setState((prevState) => ({
                                ...prevState,
                                editNumberRecord: sipNumberRecord.data,
                                isEditNumberStatusModalVisible: true,
                              }));
                            });
                          }
                        }}
                      >
                        <span style={{ userSelect: "text" }}>
                          <TextHighlighter
                            bodyText={cellText}
                            searchText={searchText}
                            ignoreChars={ignoreChars}
                          />
                        </span>
                      </Button>
                      <div className="absolute top-1 right-0 w-[32px]">
                        <Tooltip title={dataRow.comment}>
                          <Badge
                            count={
                              dataRow.comment && (
                                <span>
                                  <InfoCircleFilled style={{ color: "blue" }} />
                                </span>
                              )
                            }
                            className="-mt-2 right-0 float-right"
                            color={dataRow.comment ? "blue" : undefined}
                            title={""}
                          ></Badge>
                        </Tooltip>
                      </div>
                    </div>
                  ) : (
                    cellText
                  );
                },
              },
              {
                titleText: "Username/Meeting Room/DL",
                dataIndex: "usernameOrMeetingRoomNameX",
                render: ({
                  dataRow,
                  cellText = "",
                  searchText = "",
                  ignoreChars = "",
                }) => {
                  return (
                    <Tooltip title={dataRow.usernameOrMeetingRoomNameXType}>
                      <>
                        <TextHighlighter
                          bodyText={cellText}
                          searchText={searchText}
                          ignoreChars={ignoreChars}
                        />
                      </>
                    </Tooltip>
                  );
                },
              },
              {
                titleText: "Assigned Date",
                dataIndex: "dateTimeAddedX",
              },
              {
                titleText: "Created By",
                dataIndex: "createdBy",
              },

              {
                titleText: "Last Error",
                dataIndex: "lastError",
                width: "40%",
              },
            ],
          }}
        />
        <EditNumberStatusFormModal
          isVisible={isEditNumberStatusModalVisible}
          handleClose={(refreshData) =>
            setState((prevState) => ({
              ...prevState,
              isEditNumberStatusModalVisible: false,
              ...(refreshData ? { refreshDataToggle: !refreshDataToggle } : {}),
            }))
          }
          sipNumberRecord={editNumberRecord}
        />
      </div>
    </div>
  );
}

export default ErrorsPage;
